import { format, formatDistanceToNow } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RoleLabel } from "../../auth/types";
import { bulkResetFailedCredentials, useProvisionJobLogs } from "../hooks";
import type { ProvisionJobSummary } from "../types";
import "../../../App.css";

interface JobCardProps {
  job: ProvisionJobSummary;
}

const downloadCsvBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const statusStyles: Record<ProvisionJobSummary["status"], { bg: string; color: string; label: string }> = {
  pending: { bg: "#fef3c7", color: "#b45309", label: "Pending" },
  in_progress: { bg: "#dbeafe", color: "#1d4ed8", label: "In progress" },
  completed: { bg: "#dcfce7", color: "#15803d", label: "Completed" },
  failed: { bg: "#fee2e2", color: "#b91c1c", label: "Failed" }
};

/** Notes longer than this show a “View full notes” link (card still uses 3-line clamp). */
const NOTES_MORE_LINK_MIN_LEN = 90;

const logsPath = (jobId: string) => `/jobs/${jobId}/logs`;

const JobCard = ({ job }: JobCardProps) => {
  const statusStyle = statusStyles[job.status];
  const logsQuery = useProvisionJobLogs(job.id, true, { refetchInterval: 30_000 });
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const handleBulkReset = async () => {
    setResetError(null);
    setResetting(true);
    try {
      const blob = await bulkResetFailedCredentials(job.id);
      downloadCsvBlob(blob, `bulk-reset-${job.id}.csv`);
    } catch (err) {
      const maybeAxios = err as {
        response?: { data?: unknown; status?: number };
        message?: string;
      };
      const data = maybeAxios?.response?.data as { detail?: unknown } | undefined;
      const detail =
        typeof data?.detail === "string"
          ? data.detail
          : maybeAxios?.message || "Bulk reset failed. Try again.";
      setResetError(detail);
    } finally {
      setResetting(false);
    }
  };

  const logSummary = useMemo(() => {
    const logs = logsQuery.data ?? [];
    return logs.reduce(
      (acc, row) => {
        acc.total += 1;
        if (row.status === "completed") acc.completed += 1;
        else if (row.status === "failed") acc.failed += 1;
        else acc.other += 1;
        return acc;
      },
      { total: 0, completed: 0, failed: 0, other: 0 }
    );
  }, [logsQuery.data]);

  const successPct =
    logSummary.total > 0 ? Math.round((logSummary.completed / logSummary.total) * 100) : null;

  const pct = (n: number) => (logSummary.total > 0 ? (n / logSummary.total) * 100 : 0);

  const notes = job.description?.trim() || job.details?.trim() || null;
  const showNotesMoreLink = Boolean(notes && notes.length > NOTES_MORE_LINK_MIN_LEN);
  const detailsTo = logsPath(job.id);

  return (
    <article
      style={{
        backgroundColor: "#fff",
        borderRadius: "1rem",
        border: "1px solid #e2e8f0",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div
        style={{
          padding: "1.25rem 1.35rem",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          gap: "1rem"
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "0.75rem",
            flexWrap: "wrap"
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
              {format(new Date(job.createdAt), "MM.dd.yyyy")}
            </div>
            <div
              style={{
                marginTop: "0.35rem",
                fontSize: "1rem",
                fontWeight: 700,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
              title={job.name}
            >
              {job.name}
            </div>
            <div
              style={{
                marginTop: "0.2rem",
                fontSize: "0.78rem",
                color: "#94a3b8",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
              }}
              title={job.id}
            >
              {job.id}
            </div>
          </div>
          <span
            style={{
              padding: "0.3rem 0.7rem",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 700,
              backgroundColor: statusStyle.bg,
              color: statusStyle.color,
              whiteSpace: "nowrap",
              flexShrink: 0
            }}
          >
            {statusStyle.label}
          </span>
        </header>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem", fontSize: "0.82rem", color: "#475569" }}>
          <div style={{ display: "flex", gap: "0.45rem", alignItems: "baseline" }}>
            <span style={{ color: "#94a3b8", fontWeight: 600, minWidth: "4.5rem" }}>Created</span>
            <span style={{ fontWeight: 500 }}>
              {format(new Date(job.createdAt), "MMM d, yyyy h:mm a")}{" "}
              <span style={{ color: "#94a3b8" }}>({formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })})</span>
            </span>
          </div>
          <div style={{ display: "flex", gap: "0.45rem", alignItems: "flex-start" }}>
            <span style={{ color: "#94a3b8", fontWeight: 600, minWidth: "4.5rem" }}>Created by</span>
            <span>
              <span style={{ fontWeight: 600, color: "#334155" }}>{job.createdBy.name}</span>
              <span style={{ display: "block", fontSize: "0.8rem", color: "#64748b" }}>{job.createdBy.email}</span>
            </span>
          </div>
          <div style={{ display: "flex", gap: "0.45rem", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: "#94a3b8", fontWeight: 600, minWidth: "4.5rem" }}>Access</span>
            <span
              style={{
                display: "inline-block",
                padding: "0.2rem 0.55rem",
                borderRadius: "999px",
                backgroundColor: "#dcfce7",
                color: "#166534",
                fontWeight: 700,
                border: "1px solid #86efac",
                fontSize: "0.78rem"
              }}
            >
              {RoleLabel[job.createdBy.role] ?? job.createdBy.role}
            </span>
          </div>
          {notes ? (
            <div style={{ display: "flex", gap: "0.45rem", alignItems: "flex-start", minWidth: 0 }}>
              <span style={{ color: "#94a3b8", fontWeight: 600, minWidth: "4.5rem", flexShrink: 0 }}>Notes</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="job-card-notes-preview" title={notes}>
                  {notes}
                </p>
                {showNotesMoreLink ? (
                  <Link to={detailsTo} className="job-card-notes-more">
                    View full notes →
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ flex: "1 1 0", minHeight: "0.5rem" }} aria-hidden />

        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: "0.68rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "#94a3b8",
              fontWeight: 800,
              marginBottom: "0.4rem"
            }}
          >
            Row results
          </div>
          {logsQuery.isLoading ? (
            <span style={{ fontSize: "0.85rem", color: "#94a3b8" }}>Loading counts…</span>
          ) : logsQuery.isError ? (
            <span style={{ fontSize: "0.85rem", color: "#b45309" }}>Open details to load row results.</span>
          ) : logSummary.total === 0 ? (
            <span style={{ fontSize: "0.85rem", color: "#94a3b8" }}>No row logs yet.</span>
          ) : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center" }}>
                <span
                  style={{
                    padding: "0.25rem 0.55rem",
                    borderRadius: "0.45rem",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    backgroundColor: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #e2e8f0"
                  }}
                  title={job.totalRows ? `${logSummary.total} of ${job.totalRows} rows processed` : undefined}
                >
                  {job.totalRows && job.totalRows > 0
                    ? `${logSummary.total} / ${job.totalRows} processed`
                    : `${logSummary.total} total`}
                </span>
                {(job.credentialEmailsFailed ?? 0) > 0 ? (
                  <>
                    <span
                      style={{
                        padding: "0.25rem 0.55rem",
                        borderRadius: "0.45rem",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        backgroundColor: "#fffbeb",
                        color: "#9a3412",
                        border: "1px solid #fcd34d"
                      }}
                      title="Credentials email did not deliver for these rows. Use the bulk-reset action to issue new temporary passwords."
                    >
                      {job.credentialEmailsFailed} credential {job.credentialEmailsFailed === 1 ? "email" : "emails"} bounced
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleBulkReset()}
                      disabled={resetting}
                      style={{
                        padding: "0.25rem 0.6rem",
                        borderRadius: "0.45rem",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        backgroundColor: "#1d4ed8",
                        color: "#ffffff",
                        border: "1px solid #1d4ed8",
                        cursor: resetting ? "not-allowed" : "pointer",
                        opacity: resetting ? 0.7 : 1
                      }}
                      title="Generate fresh temp passwords for every bounced row and download as CSV."
                    >
                      {resetting ? "Resetting…" : "Bulk reset credentials"}
                    </button>
                    {resetError ? (
                      <span style={{ fontSize: "0.75rem", color: "#b91c1c", fontWeight: 600, marginLeft: "0.25rem" }}>
                        {resetError}
                      </span>
                    ) : null}
                  </>
                ) : null}
                <span
                  style={{
                    padding: "0.25rem 0.55rem",
                    borderRadius: "0.45rem",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    backgroundColor: "#ecfdf5",
                    color: "#047857",
                    border: "1px solid #a7f3d0"
                  }}
                >
                  {logSummary.completed} completed
                </span>
                <span
                  style={{
                    padding: "0.25rem 0.55rem",
                    borderRadius: "0.45rem",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    backgroundColor: "#fef2f2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca"
                  }}
                >
                  {logSummary.failed} failed
                </span>
                {logSummary.other > 0 ? (
                  <span
                    style={{
                      padding: "0.25rem 0.55rem",
                      borderRadius: "0.45rem",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      backgroundColor: "#f8fafc",
                      color: "#64748b",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    {logSummary.other} other
                  </span>
                ) : null}
              </div>
              <div className="job-card-progress-track" aria-hidden={logSummary.total === 0}>
                {logSummary.total > 0 ? (
                  <>
                    {logSummary.completed > 0 ? (
                      <div
                        className="job-card-progress-seg"
                        style={{ width: `${pct(logSummary.completed)}%`, background: "#22c55e" }}
                      />
                    ) : null}
                    {logSummary.failed > 0 ? (
                      <div
                        className="job-card-progress-seg"
                        style={{ width: `${pct(logSummary.failed)}%`, background: "#f87171" }}
                      />
                    ) : null}
                    {logSummary.other > 0 ? (
                      <div
                        className="job-card-progress-seg"
                        style={{ width: `${pct(logSummary.other)}%`, background: "#cbd5e1" }}
                      />
                    ) : null}
                  </>
                ) : null}
              </div>
              {successPct !== null ? (
                <div style={{ marginTop: "0.35rem", fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>
                  {successPct}% successful
                </div>
              ) : null}
            </>
          )}
        </div>

        <div style={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", paddingTop: "0.15rem" }}>
          <Link
            to={detailsTo}
            style={{
              padding: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "0.88rem",
              color: "#2563eb",
              textDecoration: "none"
            }}
          >
            View details →
          </Link>
        </div>
      </div>
    </article>
  );
};

export default JobCard;
